import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReactCompareImage from 'react-compare-image';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { brand } from '../theme';
import { company } from '../data/company';

const storage = getStorage();
const beforeImageRef = ref(storage, 'before-after-images/before.jpg');
const afterImageRef = ref(storage, 'before-after-images/after.jpg');

const useStyles = makeStyles((theme) => ({
  card: {
    overflow: 'hidden',
    border: `1px solid ${brand.line}`,
    maxWidth: 980,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${brand.line}`,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  hint: {
    color: theme.palette.text.secondary,
  },
  compare: {
    backgroundColor: '#EDEAE3',
    lineHeight: 0,
  },
  placeholder: {
    position: 'relative',
    paddingTop: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EDEAE3',
    lineHeight: 1.5,
  },
  placeholderText: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  unavailable: {
    padding: theme.spacing(6, 3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    backgroundColor: '#FBFAF7',
    '& a': {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },
}));

export default function BeforeAfter() {
  const classes = useStyles();
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Download each photo before showing the slider. The slider stays
    // collapsed if either image fails, so we show a message instead.
    const preload = (url) => new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ url, ratio: img.naturalWidth / img.naturalHeight });
      img.onerror = () => reject(new Error(`Could not load ${url}`));
      img.src = url;
    });

    // Retry once, since storage downloads can fail intermittently.
    const load = (imageRef, setImage) => getDownloadURL(imageRef)
      .then((url) => preload(url).catch(() => preload(url)))
      .then((image) => {
        if (!cancelled) setImage(image);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setFailed(true);
      });

    load(beforeImageRef, setBeforeImage);
    load(afterImageRef, setAfterImage);

    return () => {
      cancelled = true;
    };
  }, []);

  const ready = !failed && beforeImage && afterImage;
  // Match the slider's "wider" sizing, then cap its height at 75% of the
  // screen so tall portrait photos don't overflow the viewport.
  const sliderRatio = ready ? Math.max(beforeImage.ratio, afterImage.ratio) : 1;
  const sliderStyle = { maxWidth: `min(100%, calc(75vh * ${sliderRatio}))`, margin: '0 auto' };

  return (
    <Paper className={classes.card}>
      <Box className={classes.header}>
        <Typography variant="h5" component="h3">Before &amp; after</Typography>
      </Box>
      <div className={classes.compare}>
        {ready ? (
          <div style={sliderStyle}>
            <ReactCompareImage
              leftImage={beforeImage.url}
              rightImage={afterImage.url}
              leftImageLabel="Before"
              rightImageLabel="After"
              sliderLineColor="#FFFFFF"
              handleSize={44}
              aspectRatio="wider"
            />
          </div>
        ) : failed ? (
          <Typography variant="body2" component="div" className={classes.unavailable}>
            Project photos are being updated. Call <a href={company.phoneHref}>{company.phoneDisplay}</a> to
            see examples of our work.
          </Typography>
        ) : (
          <div className={classes.placeholder}>
            <Typography variant="body2" className={classes.placeholderText}>
              Loading photos…
            </Typography>
          </div>
        )}
      </div>
    </Paper>
  );
}
